import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Trash2, Search } from "lucide-react";
import { staffApi } from "@client/api/staff";
import { StaffShell } from "@client/components/StaffShell";
import { Badge } from "@client/components/ui/badge";
import { Button } from "@client/components/ui/button";
import { Input } from "@client/components/ui/input";
import { useConfirm } from "@client/components/confirm-dialog";

export const Route = createFileRoute("/_authenticated/activity")({
  component: ActivityPage,
});

function ActivityPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [activitySearch, setActivitySearch] = useState("");
  const [auditSearch, setAuditSearch] = useState("");

  const { data } = useQuery({
    queryKey: ["activity"],
    refetchInterval: 8000,
    queryFn: staffApi.activity,
  });
  const activity = data?.activity;
  const audit = data?.audit;

  const clearLogsMut = useMutation({
    mutationFn: () => staffApi.clearActivityLogs(),
    onSuccess: () => {
      toast.success("Activity and audit logs cleared");
      qc.invalidateQueries({ queryKey: ["activity"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteActivityMut = useMutation({
    mutationFn: (id: string) => staffApi.deleteActivityLog(id),
    onSuccess: () => {
      toast.success("Log item deleted");
      qc.invalidateQueries({ queryKey: ["activity"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteAuditMut = useMutation({
    mutationFn: (id: string) => staffApi.deleteAuditLog(id),
    onSuccess: () => {
      toast.success("Audit item deleted");
      qc.invalidateQueries({ queryKey: ["activity"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const filteredActivity = (activity ?? []).filter((e) => {
    if (!activitySearch.trim()) return true;
    const q = activitySearch.toLowerCase();
    const refereeName =
      (e.referee_attempts as { full_name: string } | null)?.full_name ?? "";
    const sessionTitle =
      (e.exam_sessions as { title: string } | null)?.title ?? "";
    return (
      e.type.toLowerCase().includes(q) ||
      refereeName.toLowerCase().includes(q) ||
      sessionTitle.toLowerCase().includes(q)
    );
  });

  const filteredAudit = (audit ?? []).filter((e) => {
    if (!auditSearch.trim()) return true;
    const q = auditSearch.toLowerCase();
    const actorEmail = e.actor_email ?? "";
    const action = e.action ?? "";
    const targetType = e.target_type ?? "";
    return (
      action.toLowerCase().includes(q) ||
      actorEmail.toLowerCase().includes(q) ||
      targetType.toLowerCase().includes(q)
    );
  });

  return (
    <StaffShell
      title="Activity Logs"
      subtitle="Referee behaviour and staff audit trail"
      actions={
        activity?.length || audit?.length ? (
          <Button
            variant="destructive"
            size="sm"
            className="gap-1 text-xs"
            onClick={async () => {
              if (
                await confirm({
                  title: "Clear all logs?",
                  description:
                    "This clears all activity and audit logs. This action cannot be undone.",
                  confirmText: "Clear logs",
                  destructive: true,
                })
              ) {
                clearLogsMut.mutate();
              }
            }}
            disabled={clearLogsMut.isPending}
          >
            <Trash2 className="h-3.5 w-3.5" />
            Clear Logs
          </Button>
        ) : null
      }
    >
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="panel p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="hall-heading text-lg font-bold">Referee activity</h2>
            <div className="relative w-full sm:w-48">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Filter activity..."
                className="h-8 pl-8 text-xs"
                value={activitySearch}
                onChange={(e) => setActivitySearch(e.target.value)}
              />
            </div>
          </div>
          <ul className="mt-4 space-y-2 text-sm">
            {filteredActivity.map((e) => (
              <li
                key={e.id}
                className="flex items-center justify-between gap-3 border-b border-border pb-2 group"
              >
                <div className="min-w-0 flex-1">
                  <Badge variant="secondary">{e.type}</Badge>
                  <p className="mt-1 text-xs text-muted-foreground truncate">
                    {(e.referee_attempts as { full_name: string } | null)
                      ?.full_name ?? "System"}{" "}
                    ·{" "}
                    {(e.exam_sessions as { title: string } | null)?.title ??
                      "—"}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="whitespace-nowrap text-xs text-muted-foreground">
                    {new Date(e.created_at).toLocaleString()}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    onClick={async () => {
                      if (
                        await confirm({
                          title: "Delete entry?",
                          description: "Delete this activity log entry?",
                          confirmText: "Delete",
                          destructive: true,
                        })
                      ) {
                        deleteActivityMut.mutate(e.id);
                      }
                    }}
                    disabled={deleteActivityMut.isPending}
                    title="Delete entry"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </li>
            ))}
            {!filteredActivity.length ? (
              <li className="text-muted-foreground text-xs py-2">
                {activitySearch
                  ? "No matching activity found."
                  : "No activity yet."}
              </li>
            ) : null}
          </ul>
        </div>

        <div className="panel p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="hall-heading text-lg font-bold">
              Staff audit trail
            </h2>
            <div className="relative w-full sm:w-48">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Filter by email / action..."
                className="h-8 pl-8 text-xs"
                value={auditSearch}
                onChange={(e) => setAuditSearch(e.target.value)}
              />
            </div>
          </div>
          <ul className="mt-4 space-y-2 text-sm">
            {filteredAudit.map((e) => (
              <li
                key={e.id}
                className="flex items-center justify-between gap-3 border-b border-border pb-2 group"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-foreground truncate">
                    {e.action}
                  </p>
                  <p className="text-xs font-mono text-muted-foreground truncate">
                    {e.actor_email ?? "—"}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="whitespace-nowrap text-xs text-muted-foreground">
                    {new Date(e.created_at).toLocaleString()}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    onClick={async () => {
                      if (
                        await confirm({
                          title: "Delete entry?",
                          description: "Delete this audit entry?",
                          confirmText: "Delete",
                          destructive: true,
                        })
                      ) {
                        deleteAuditMut.mutate(e.id);
                      }
                    }}
                    disabled={deleteAuditMut.isPending}
                    title="Delete entry"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </li>
            ))}
            {!filteredAudit.length ? (
              <li className="text-muted-foreground text-xs py-2">
                {auditSearch
                  ? "No matching audit logs found."
                  : "No audit entries yet."}
              </li>
            ) : null}
          </ul>
        </div>
      </div>
    </StaffShell>
  );
}
